import React, { useState, useEffect, useContext } from "react";
import {
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  FileText,
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  AlertCircle,
  Stethoscope,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  CheckCheck,
  CheckSquare,
} from "lucide-react";

// Import your API functions
import {
  updateAppointment,
  deleteAppointment,
  confirmAppointment,
  cancelAppointment,
  completeAppointment,
} from "../../../services/appointmentApi";

// Import AuthContext
import { AuthContext } from "../../../context/AuthContext";

// Import the new EditAppointmentModal
import EditAppointmentModal from "./EditAppointmentModal";
import { Link } from "react-router-dom";

const AllAppointmentForDoctor = () => {
  // Get context functions and state
  const {
    loginUser,
    getAppointments,
    allAppointments,
    allAppointmentsLoading,
    allAppointmentsError,
  } = useContext(AuthContext);

  // Local state for filtered appointments and UI
  const [doctorAppointments, setDoctorAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    type: "",
    date: "",
    startDate: "",
    endDate: "",
    dateFilter: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAppointment, setEditAppointment] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10); // Items per page

  const [statsData, setStatsData] = useState({
    total: 0,
    today: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
  });

  // Static data for dropdowns
  const appointmentTypes = [
    "checkup",
    "vaccination",
    "surgery",
    "emergency",
    "grooming",
    "dental",
    "consultation",
  ];

  const statuses = ["pending", "confirmed", "cancelled", "completed"];

  // Quick date filter options
  const dateFilterOptions = [
    { value: "", label: "All Dates" },
    { value: "today", label: "Today" },
    { value: "tomorrow", label: "Tomorrow" },
    { value: "this_week", label: "This Week" },
    { value: "next_week", label: "Next Week" },
    { value: "this_month", label: "This Month" },
    { value: "next_month", label: "Next Month" },
  ];

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Fetch appointments specifically for this doctor
  const fetchDoctorAppointments = async (page = 1, additionalParams = {}) => {
    try {
      const queryParams = {
        doctor: loginUser.id,
        page: page,
        limit: pageSize,
        ...additionalParams,
      };

      console.log("loginUser---", loginUser);

      const result = await getAppointments(queryParams);
      console.log("fetchDoctorAppointments result: ", result);
    } catch (error) {
      console.error("Error fetching doctor appointments:", error);
      setErrorMessage("Failed to fetch appointments");
    }
  };

  // Get doctor's appointments on component mount
  useEffect(() => {
    if (loginUser?.id) {
      fetchDoctorAppointments(1);
    }
  }, [loginUser?.id]);

  // Update local state when context appointments change
  useEffect(() => {
    if (allAppointments) {
      console.log("allAppointments updated:", allAppointments);

      // Handle the API response structure
      if (allAppointments.success && allAppointments.data) {
        setDoctorAppointments(allAppointments.data);
        setFilteredAppointments(allAppointments.data);
        setCurrentPage(allAppointments.currentPage || 1);
        setTotalPages(allAppointments.totalPages || 1);
        setTotalCount(allAppointments.total || 0);
        calculateStats(allAppointments.data);
      } else if (Array.isArray(allAppointments)) {
        // Fallback for array response
        setDoctorAppointments(allAppointments);
        setFilteredAppointments(allAppointments);
        calculateStats(allAppointments);
      }
    }
  }, [allAppointments]);

  // Calculate appointment statistics
  const calculateStats = (appointments) => {
    if (!appointments || !Array.isArray(appointments)) {
      setStatsData({
        total: 0,
        today: 0,
        pending: 0,
        confirmed: 0,
        completed: 0,
      });
      return;
    }

    const today = new Date().toISOString().split("T")[0];

    const stats = {
      total: appointments.length,
      today: appointments.filter((apt) => apt.date === today).length,
      pending: appointments.filter((apt) => apt.status === "pending").length,
      confirmed: appointments.filter((apt) => apt.status === "confirmed")
        .length,
      completed: appointments.filter((apt) => apt.status === "completed")
        .length,
    };

    setStatsData(stats);
  };

  // Helper function to get date range based on filter
  const getDateRange = (dateFilter) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    switch (dateFilter) {
      case "today":
        return {
          start: today.toISOString().split("T")[0],
          end: today.toISOString().split("T")[0],
        };
      case "tomorrow":
        return {
          start: tomorrow.toISOString().split("T")[0],
          end: tomorrow.toISOString().split("T")[0],
        };
      case "this_week":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          start: startOfWeek.toISOString().split("T")[0],
          end: endOfWeek.toISOString().split("T")[0],
        };
      case "next_week":
        const nextWeekStart = new Date(today);
        nextWeekStart.setDate(today.getDate() + (7 - today.getDay()));
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        return {
          start: nextWeekStart.toISOString().split("T")[0],
          end: nextWeekEnd.toISOString().split("T")[0],
        };
      case "this_month":
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0
        );
        return {
          start: startOfMonth.toISOString().split("T")[0],
          end: endOfMonth.toISOString().split("T")[0],
        };
      case "next_month":
        const nextMonthStart = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          1
        );
        const nextMonthEnd = new Date(
          today.getFullYear(),
          today.getMonth() + 2,
          0
        );
        return {
          start: nextMonthStart.toISOString().split("T")[0],
          end: nextMonthEnd.toISOString().split("T")[0],
        };
      default:
        return null;
    }
  };

  // Apply filters with server-side filtering for better performance
  const applyFiltersAndFetch = async (page = 1, resetPage = false) => {
    const filterParams = {};

    // Add search filter
    if (filters.search.trim()) {
      filterParams.search = filters.search.trim();
    }

    // Add status filter
    if (filters.status) {
      filterParams.status = filters.status;
    }

    // Add type filter
    if (filters.type) {
      filterParams.type = filters.type;
    }

    // Add date filters
    if (filters.dateFilter) {
      const dateRange = getDateRange(filters.dateFilter);
      if (dateRange) {
        filterParams.startDate = dateRange.start;
        filterParams.endDate = dateRange.end;
      }
    } else if (filters.date) {
      filterParams.date = filters.date;
    } else if (filters.startDate && filters.endDate) {
      filterParams.startDate = filters.startDate;
      filterParams.endDate = filters.endDate;
    } else if (filters.startDate) {
      filterParams.startDate = filters.startDate;
    } else if (filters.endDate) {
      filterParams.endDate = filters.endDate;
    }

    const targetPage = resetPage ? 1 : page;
    await fetchDoctorAppointments(targetPage, filterParams);
  };

  // Apply filters when filter values change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loginUser?.id) {
        applyFiltersAndFetch(1, true); // Reset to page 1 when filters change
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters, loginUser?.id]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };

      // Clear conflicting filters
      if (key === "dateFilter" && value) {
        newFilters.date = "";
        newFilters.startDate = "";
        newFilters.endDate = "";
      } else if (key === "date" && value) {
        newFilters.dateFilter = "";
        newFilters.startDate = "";
        newFilters.endDate = "";
      } else if ((key === "startDate" || key === "endDate") && value) {
        newFilters.dateFilter = "";
        newFilters.date = "";
      }

      return newFilters;
    });
  };

  // Clear filters function
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      type: "",
      date: "",
      startDate: "",
      endDate: "",
      dateFilter: "",
    });
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      applyFiltersAndFetch(newPage);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowModal(true);
  };

  const handleStatusChangeWithConfirmation = (
    appointmentId,
    currentStatus,
    newStatus
  ) => {
    // Don't show confirmation if status hasn't actually changed
    if (currentStatus === newStatus) {
      return;
    }

    const statusLabels = {
      pending: "Pending",
      confirmed: "Confirmed",
      cancelled: "Cancelled",
      completed: "Completed",
    };

    const currentStatusLabel = statusLabels[currentStatus] || currentStatus;
    const newStatusLabel = statusLabels[newStatus] || newStatus;

    const confirmationMessage = `Are you sure you want to change this appointment status from "${currentStatusLabel}" to "${newStatusLabel}"?`;

    if (window.confirm(confirmationMessage)) {
      handleStatusUpdate(appointmentId, newStatus);
    }
    // If user cancels, the dropdown will automatically revert since we're using defaultValue
  };

  const handleEditAppointment = (appointment) => {
    // Find the most up-to-date version of this appointment
    const currentAppointment =
      doctorAppointments.find((apt) => apt._id === appointment._id) ||
      appointment;
    setEditAppointment(currentAppointment);
    setShowEditModal(true);
  };

  const handleEditSuccess = (message) => {
    setSuccessMessage(message);
    setShowEditModal(false);
    setEditAppointment(null);

    // Force refresh the data to ensure both UI elements are updated
    applyFiltersAndFetch(currentPage);

    // If viewing details of the edited appointment, update the selected appointment
    if (
      selectedAppointment &&
      editAppointment &&
      selectedAppointment._id === editAppointment._id
    ) {
      // Update the selectedAppointment with new data after refresh
      setTimeout(() => {
        const updatedAppointment = doctorAppointments.find(
          (apt) => apt._id === selectedAppointment._id
        );
        if (updatedAppointment) {
          setSelectedAppointment(updatedAppointment);
        }
      }, 100);
    }
  };

  const handleEditError = (message) => {
    setErrorMessage(message);
  };
  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      setActionLoading(true);

      let result;
      switch (newStatus) {
        case "confirmed":
          result = await confirmAppointment(appointmentId);
          break;
        case "cancelled":
          result = await cancelAppointment(appointmentId);
          break;
        case "completed":
          result = await completeAppointment(appointmentId);
          break;
        default:
          result = await updateAppointment(appointmentId, {
            status: newStatus,
          });
      }

      setSuccessMessage(`Appointment status updated to ${newStatus}`);

      // Refresh current page after update
      await applyFiltersAndFetch(currentPage);

      // Update selected appointment if it's being viewed
      if (selectedAppointment?._id === appointmentId) {
        setSelectedAppointment((prev) => ({ ...prev, status: newStatus }));
      }

      // Update edit appointment if the same appointment is being edited
      if (editAppointment?._id === appointmentId) {
        setEditAppointment((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error("Error updating appointment status:", error);
      setErrorMessage(
        typeof error === "string"
          ? error
          : "Failed to update appointment status"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteAppointment(appointmentId);

      setSuccessMessage("Appointment deleted successfully");

      // Refresh current page after deletion
      await applyFiltersAndFetch(currentPage);

      // Close modal if deleted appointment was being viewed
      if (selectedAppointment?._id === appointmentId) {
        setShowModal(false);
        setSelectedAppointment(null);
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
      setErrorMessage(
        typeof error === "string" ? error : "Failed to delete appointment"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some((value) => value !== "");

  return (
    <div className="py-6 max-w-7xl mx-auto">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg flex items-center gap-2">
          <CheckCircle className="text-green-600" size={20} />
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage("")}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Error Message */}
      {(errorMessage || allAppointmentsError) && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-600" size={20} />
          <span className="text-red-700">
            {errorMessage || allAppointmentsError}
          </span>
          <button
            onClick={() => setErrorMessage("")}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Stethoscope className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">My Appointments</h1>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today</p>
              <p className="text-2xl font-bold text-green-600">
                {statsData.today}
              </p>
            </div>
            <Clock className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {statsData.pending}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Confirmed</p>
              <p className="text-2xl font-bold text-blue-600">
                {statsData.confirmed}
              </p>
            </div>
            <User className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-purple-600">
                {statsData.completed}
              </p>
            </div>
            <FileText className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by pet name, owner, type, or notes..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          {/* Quick Date Filter */}
          <div className="min-w-[200px]">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.dateFilter}
              onChange={(e) => handleFilterChange("dateFilter", e.target.value)}
            >
              {dateFilterOptions?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter size={20} />
            More Filters
          </button>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-800 bg-red-50 rounded-lg hover:bg-red-100"
            >
              <X size={20} />
              Clear All
            </button>
          )}
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {statuses?.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                >
                  <option value="">All Types</option>
                  {appointmentTypes?.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Specific Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specific Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.date}
                  onChange={(e) => handleFilterChange("date", e.target.value)}
                  disabled={filters.dateFilter !== ""}
                />
              </div>

              {/* Date Range Start */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                  disabled={filters.dateFilter !== "" || filters.date !== ""}
                />
              </div>

              {/* Date Range End */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                  disabled={filters.dateFilter !== "" || filters.date !== ""}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <p className="text-gray-600">
            Showing {doctorAppointments?.length} of {totalCount} appointments
            {totalPages > 1 && (
              <span className="ml-2 text-sm text-gray-500">
                (Page {currentPage} of {totalPages})
              </span>
            )}
          </p>
          {hasActiveFilters && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-blue-600">Active filters:</span>
              <div className="flex gap-1">
                {filters.search && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                    Search: {filters.search}
                  </span>
                )}
                {filters.status && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                    Status: {filters.status}
                  </span>
                )}
                {filters.type && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                    Type: {filters.type}
                  </span>
                )}
                {filters.dateFilter && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                    Date:{" "}
                    {
                      dateFilterOptions.find(
                        (opt) => opt.value === filters.dateFilter
                      )?.label
                    }
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        <button
          onClick={() => applyFiltersAndFetch(currentPage)}
          disabled={allAppointmentsLoading}
          className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1"
        >
          <svg
            className={`w-4 h-4 ${
              allAppointmentsLoading ? "animate-spin" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden relative">
        {allAppointmentsLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Loading appointments...</span>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pet & Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Change Status
                </th>
                <th className="py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Meet Link
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {doctorAppointments?.map((appointment) => (
                <tr key={appointment._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {appointment.pets?.name?.charAt(0) || "P"}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.pets?.name || "Unknown Pet"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {appointment.pets?.ownerId?.userDetails?.name ||
                            appointment.pets?.owner?.name ||
                            "Unknown Owner"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {appointment.type
                        ? appointment.type.charAt(0).toUpperCase() +
                          appointment.type.slice(1)
                        : ""}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar size={16} className="text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900 mr-2">
                        {formatDate(appointment.date)}
                      </span>
                      <Clock size={16} className="text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">
                        {formatTime(appointment.time)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          appointment.status
                        )}`}
                      >
                        {appointment.status.charAt(0).toUpperCase() +
                          appointment.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      key={`status-${appointment._id}-${appointment.status}`}
                      onChange={(e) => {
                        handleStatusChangeWithConfirmation(
                          appointment._id,
                          appointment.status,
                          e.target.value
                        );
                      }}
                      className="text-xs border border-gray-300 rounded px-2 py-1"
                      disabled={actionLoading}
                      defaultValue={appointment.status} // Changed from value to defaultValue
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewDetails(appointment)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleEditAppointment(appointment)}
                        className="text-green-600 hover:text-green-900"
                        title="Edit"
                        disabled={actionLoading}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteAppointment(appointment._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                        disabled={actionLoading}
                      >
                        <Trash2 size={18} />
                      </button>
                      <Link
                        to={`/dashboard/check-up/${appointment._id}`}
                        className="text-blue-600 hover:text-blue-900"
                        title="Start Check-up"
                        disabled={actionLoading}
                      >
                        <CheckSquare size={18} />
                      </Link>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {appointment.meetLink ? (
                      <div className="flex items-center gap-1">
                        {/* <span className="text-green-600 text-xs">✓ Ready</span> */}

                        <a
                          href={appointment.meetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-xs"
                          title="Join Meeting"
                        >
                          ✓ join Meeting
                        </a>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">No link</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {doctorAppointments?.length === 0 && !allAppointmentsLoading && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No appointments found</p>
              <p className="text-sm">
                {hasActiveFilters
                  ? "Try adjusting your filters"
                  : "You don't have any appointments yet"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span>
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
              results
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Previous Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || allAppointmentsLoading}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {/* Show first page */}
              {currentPage > 3 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 4 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                </>
              )}

              {/* Show pages around current page */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (page) =>
                    page >= Math.max(1, currentPage - 2) &&
                    page <= Math.min(totalPages, currentPage + 2)
                )
                .map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    disabled={allAppointmentsLoading}
                    className={`px-3 py-2 text-sm rounded-lg border disabled:cursor-not-allowed ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}

              {/* Show last page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            {/* Next Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || allAppointmentsLoading}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Appointment Details Modal */}
      {showModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Appointment Details
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pet Name
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.pets?.name || "Unknown Pet"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.pets?.ownerId?.userDetails?.name ||
                      selectedAppointment.pets?.owner?.name ||
                      "Unknown Owner"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pet Species
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.pets?.species || "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pet Breed
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.pets?.breed || "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Type
                  </label>
                  <p className="text-gray-900">{selectedAppointment.type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <p className="text-gray-900">
                    {formatDate(selectedAppointment.date)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time
                  </label>
                  <p className="text-gray-900">
                    {formatTime(selectedAppointment.time)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      selectedAppointment.status
                    )}`}
                  >
                    {selectedAppointment.status.charAt(0).toUpperCase() +
                      selectedAppointment.status.slice(1)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.duration || "30 minutes"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <p className="text-gray-900">
                    {formatDate(selectedAppointment.createdAt)}
                  </p>
                </div>
              </div>

              {/* Appointment Notes */}
              {selectedAppointment.notes && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Notes
                  </label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {selectedAppointment.notes}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
                <div className="flex space-x-2">
                  {selectedAppointment.status === "pending" && (
                    <button
                      onClick={() =>
                        handleStatusUpdate(selectedAppointment._id, "confirmed")
                      }
                      disabled={actionLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Confirm
                    </button>
                  )}
                  {selectedAppointment.status === "confirmed" && (
                    <button
                      onClick={() =>
                        handleStatusUpdate(selectedAppointment._id, "completed")
                      }
                      disabled={actionLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Mark Complete
                    </button>
                  )}
                  {(selectedAppointment.status === "pending" ||
                    selectedAppointment.status === "confirmed") && (
                    <button
                      onClick={() =>
                        handleStatusUpdate(selectedAppointment._id, "cancelled")
                      }
                      disabled={actionLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      handleEditAppointment(selectedAppointment);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Edit Appointment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Appointment Modal */}
      <EditAppointmentModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditAppointment(null);
        }}
        appointment={editAppointment}
        onSuccess={handleEditSuccess}
        onError={handleEditError}
      />
    </div>
  );
};

export default AllAppointmentForDoctor;
